import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, authAPI } from '../api';
import { getTranslation } from '../i18n';
import { overlayStyle, modalStyle, inputStyle, labelStyle } from './ClientSpace/utils';

export default function ClientsList() {
  const navigate = useNavigate();
  document.title = 'Maestra - Client Space';

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState('en');
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    authAPI.getSettings().then(({ data }) => setLang(data.language || 'en')).catch(() => {});
    fetchClients();
  }, []);

  const t = getTranslation(lang);

  const fetchClients = async () => {
    try {
      const { data } = await clientsAPI.list();
      setClients(data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };

  const handleExportZip = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await clientsAPI.exportZip();
      const res = await clientsAPI.downloadExport(data.download_token);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || 'Maestra_Client_Space.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setToast(t.cs_export_failed || 'Export failed — please try again.');
      setTimeout(() => setToast(null), 3000);
    } finally { setExporting(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await clientsAPI.create(form);
      setShowCreate(false);
      setForm({ name: '', company: '', email: '', phone: '', address: '' });
      navigate(`/clients/${data.id}`);
    } catch (err) { /* noop */ } finally { setSaving(false); }
  };

  return (
    <div className="dashboard fade-in" data-testid="clients-page">
      <header className="dashboard-header">
        <div>
          <button
            type="button"
            data-testid="clients-back-dashboard"
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'transparent', border: 'none', padding: 0, marginBottom: 18, cursor: 'pointer',
              fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: 'var(--kahla-text-light)',
            }}
          >← {t.cs_back_dashboard || 'Workspace'}</button>
          <h1 className="dashboard-title">{t.cs_title || 'Client Space'}</h1>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 15, color: 'var(--kahla-text-light)', margin: '8px 0 0', maxWidth: 560, lineHeight: 1.6,
          }}>{t.cs_subtitle || 'A private record of every collaboration.'}</p>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {clients.length > 0 && (
            <button className="btn-ghost" data-testid="export-clients-zip-btn" onClick={handleExportZip} disabled={exporting}>
              {exporting ? (t.cs_exporting || 'Preparing…') : (t.cs_export_zip || 'Export ZIP')}
            </button>
          )}
          <button className="btn-primary" data-testid="new-client-btn" onClick={() => setShowCreate(true)}>
            {t.cs_new_client || '+ New client'}
          </button>
        </div>
      </header>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : clients.length === 0 ? (
        <div className="empty-state" data-testid="clients-empty">
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 400, fontSize: 24 }}>
            {t.cs_no_clients_title || 'No clients yet'}
          </h2>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 15, color: 'var(--kahla-text-light)', maxWidth: 480, margin: '12px auto 32px', lineHeight: 1.7,
          }}>{t.cs_no_clients || 'Create your first client record — history, documents and boards will live here.'}</p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>{t.cs_new_client || '+ New client'}</button>
        </div>
      ) : (
        <>
          <div style={{ maxWidth: 860, marginBottom: 8 }}>
            <input
              data-testid="clients-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.cs_search_clients || 'Search clients…'}
              style={{
                width: '100%', maxWidth: 380, padding: '9px 0', border: 'none',
                borderBottom: '1px solid var(--kahla-border)', background: 'transparent',
                fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
                fontSize: 15, color: 'var(--kahla-charcoal)', outline: 'none',
              }}
            />
          </div>
          {(() => {
            const needle = q.trim().toLowerCase();
            const visible = needle
              ? clients.filter((c) =>
                  [c.name, c.company, c.email, c.phone]
                    .some((v) => (v || '').toLowerCase().includes(needle)))
              : clients;
            if (visible.length === 0) {
              return (
                <p data-testid="clients-search-empty" style={{
                  fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
                  fontSize: 15, color: 'var(--kahla-text-light)', padding: '32px 4px 64px',
                }}>{t.cs_clients_no_results || 'No clients match your search.'}</p>
              );
            }
            return (
        <div data-testid="clients-list" style={{ maxWidth: 860 }}>
          {visible.map((c) => (
            <div
              key={c.id}
              data-testid={`client-row-${c.id}`}
              onClick={() => navigate(`/clients/${c.id}`)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 24,
                padding: '22px 4px', borderBottom: '1px solid var(--kahla-border)', cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', minWidth: 0 }}>
                <div aria-hidden="true" style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: '#EAE3D6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Playfair Display, serif', fontSize: 15, letterSpacing: '0.05em', color: '#7d745f',
                }}>{((c.name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('') || '·').toUpperCase()}</div>
                <div style={{ minWidth: 0 }}>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 19, fontWeight: 400, color: 'var(--kahla-charcoal)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.name}
                </h3>
                {(c.company || c.email) && (
                  <p style={{ fontSize: 13, color: 'var(--kahla-text-light)', margin: '4px 0 0' }}>
                    {[c.company, c.email].filter(Boolean).join(' · ')}
                  </p>
                )}
                </div>
              </div>
              <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', whiteSpace: 'nowrap' }}>
                {c.history_count} {t.cs_meta_history || 'history'} · {c.boards_count} {t.cs_meta_boards || 'boards'}
              </span>
            </div>
          ))}
        </div>
            );
          })()}
        </>
      )}

      {showCreate && (
        <div style={overlayStyle} onClick={() => setShowCreate(false)}>
          <form style={{ ...modalStyle, maxWidth: 480 }} onClick={(e) => e.stopPropagation()} onSubmit={handleCreate} data-testid="create-client-modal">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, fontWeight: 400, margin: '0 0 24px', color: 'var(--kahla-charcoal)' }}>
              {t.cs_new_client_title || 'New client'}
            </h2>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={labelStyle}>{t.cs_client_name || 'Name'} *</label>
                <input data-testid="client-name-input" style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>{t.cs_company || 'Company (optional)'}</label>
                <input data-testid="client-company-input" style={inputStyle} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>{t.cs_email || 'Email'}</label>
                  <input data-testid="client-email-input" style={inputStyle} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>{t.cs_phone || 'Phone'}</label>
                  <input data-testid="client-phone-input" style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>{t.cs_address_optional || 'Address (optional — you can add it later)'}</label>
                <input data-testid="client-address-input" style={inputStyle} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-ghost" onClick={() => setShowCreate(false)}>{t.cs_cancel || 'Cancel'}</button>
              <button type="submit" className="btn-primary" data-testid="create-client-submit" disabled={!form.name.trim() || saving}>
                {t.cs_create || 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
